"use client";

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { STATUS_IDS } from "@/lib/constants/statuses";

interface FAQ {
  faq_id: number;
  venue_id: number;
  questions: Record<string, string>;
  answers: Record<string, string>;
  display_order: number;
  status: { id: number; name: string };
  created_at: string;
  updated_at: string;
}

interface VenueFAQFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  faq: FAQ | null;
  onSave: (faqData: {
    questions: Record<string, string>;
    answers: Record<string, string>;
    status_id?: number;
    display_order?: number;
  }) => void;
  venueLanguages: string[];
}

export function VenueFAQFormDialog({
  open,
  onOpenChange,
  faq,
  onSave,
  venueLanguages,
}: VenueFAQFormDialogProps) {
  // When editing, show all languages that have content + venue languages
  // This ensures that if a venue removes a language, existing FAQ translations are still editable
  const existingFaqLanguages = faq
    ? Object.keys(faq.questions).filter((lang) => faq.questions[lang])
    : [];
  const allLanguages = [
    ...new Set([...venueLanguages, ...existingFaqLanguages]),
  ].sort();
  const languages = allLanguages.length > 0 ? allLanguages : ["en"];
  const [faqStatuses, setFaqStatuses] = useState<
    { id: number; name: string }[]
  >([]);

  const createEmptyLangObject = (langs: string[] = languages) => {
    const obj: Record<string, string> = {};
    langs.forEach((lang) => {
      obj[lang] = "";
    });
    return obj;
  };

  const [formData, setFormData] = useState({
    questions: createEmptyLangObject(),
    answers: createEmptyLangObject(),
    status_id: STATUS_IDS.FAQ_ACTIVE,
    display_order: 0,
  });

  const [activeTab, setActiveTab] = useState(languages[0] || "en");

  // Fetch FAQ statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await fetch("/api/statuses?type=faq");
        const data = await response.json();
        if (data.success && data.data) {
          const statuses = data.data.all || [];
          setFaqStatuses(statuses);

          // Set default status to 'active' if it exists
          const activeStatus = statuses.find((s: any) => s.name === "active");
          if (activeStatus && !faq) {
            setFormData((prev) => ({ ...prev, status_id: activeStatus.id }));
          }
        }
      } catch (error) {
        console.error("Error fetching FAQ statuses:", error);
      }
    };

    fetchStatuses();
  }, []);

  useEffect(() => {
    if (faq) {
      const questions: Record<string, string> = {};
      const answers: Record<string, string> = {};

      languages.forEach((lang) => {
        questions[lang] = faq.questions[lang] || "";
        answers[lang] = faq.answers[lang] || "";
      });

      setFormData({
        questions,
        answers,
        status_id: faq.status.id as typeof STATUS_IDS.FAQ_ACTIVE,
        display_order: faq.display_order,
      });
    } else {
      // Reset form to defaults when adding new FAQ
      setFormData({
        questions: createEmptyLangObject(),
        answers: createEmptyLangObject(),
        status_id: STATUS_IDS.FAQ_ACTIVE,
        display_order: 0,
      });
      // Reset tab to first language when adding new FAQ
      setActiveTab(languages[0] || "en");
    }
  }, [faq, venueLanguages]);

  // Ensure activeTab is valid when languages change
  useEffect(() => {
    if (!languages.includes(activeTab)) {
      setActiveTab(languages[0] || "en");
    }
  }, [languages, activeTab]);

  // Reset form completely when dialog opens/closes
  useEffect(() => {
    if (open && !faq) {
      // Fully reset for new FAQ
      setFormData({
        questions: createEmptyLangObject(),
        answers: createEmptyLangObject(),
        status_id: STATUS_IDS.FAQ_ACTIVE,
        display_order: 0,
      });
      setActiveTab(languages[0] || "en");
    }
  }, [open, faq]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that at least one language has both question and answer
    const hasValidContent = languages.some(
      (lang) =>
        formData.questions[lang]?.trim() && formData.answers[lang]?.trim(),
    );

    if (!hasValidContent) {
      alert(
        `Please provide both question and answer for at least one language`,
      );
      return;
    }

    onSave(formData);
  };

  const updateField = (
    lang: string,
    field: "questions" | "answers",
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value,
      },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{faq ? "Edit FAQ" : "Add new FAQ"}</DialogTitle>
            <DialogDescription>
              {faq
                ? "Update the FAQ details below. Provide translations for the available languages."
                : "Fill in the details to create a new FAQ. Provide translations for the available languages."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList
                className={`grid w-full ${
                  languages.length === 1
                    ? "grid-cols-1"
                    : languages.length === 2
                      ? "grid-cols-2"
                      : languages.length === 3
                        ? "grid-cols-3"
                        : languages.length === 4
                          ? "grid-cols-4"
                          : "grid-cols-5"
                }`}
              >
                {languages.map((lang) => (
                  <TabsTrigger key={lang} value={lang}>
                    {lang.toUpperCase()}
                  </TabsTrigger>
                ))}
              </TabsList>

              {languages.map((lang) => (
                <TabsContent key={lang} value={lang} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor={`question-${lang}`}>
                      Question ({lang.toUpperCase()})
                    </Label>
                    <Input
                      id={`question-${lang}`}
                      value={formData.questions[lang] || ""}
                      onChange={(e) =>
                        updateField(lang, "questions", e.target.value)
                      }
                      placeholder={`Enter question in ${lang.toUpperCase()}`}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`answer-${lang}`}>
                      Answer ({lang.toUpperCase()})
                    </Label>
                    <Textarea
                      id={`answer-${lang}`}
                      value={formData.answers[lang] || ""}
                      onChange={(e) =>
                        updateField(lang, "answers", e.target.value)
                      }
                      placeholder={`Enter answer in ${lang.toUpperCase()}`}
                      rows={4}
                    />
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status_id.toString()}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    status_id: parseInt(value) as typeof STATUS_IDS.FAQ_ACTIVE,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {faqStatuses.map((status) => (
                    <SelectItem key={status.id} value={status.id.toString()}>
                      {status.name.charAt(0).toUpperCase() +
                        status.name.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_order">Display order</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    display_order: parseInt(e.target.value) || 0,
                  }))
                }
                placeholder="0"
                min="0"
              />
              <p className="text-xs text-muted-foreground">
                Lower numbers appear first. FAQs with the same order are sorted
                alphabetically.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">{faq ? "Update FAQ" : "Create FAQ"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
